import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageData } from 'src/app/pages/promote/pageContext';
import { calculateImpressions } from 'src/app/shared/utils/promote';

type ButtonAmount = {
	value: string;
	valueDisplay: string;
	label: string;
	bonusLabel?: string;
	metaText?: string;
};

export function useButtonAmounts(): ButtonAmount[] {
	const {
		data: {
			promotionSettings: { cpm, bonusCpm, bonusThreshold },
			amountChoices,
			currency: { code: currencyCode },
		},
	} = usePromotePageData();

	const { amountToBaseUnits, translatePlural, localizeNumber, translate, localizeCurrency } = useI18n();

	const impressionCountData = useCallback(
		(amount: number) => {
			const amountInBaseUnits = amountToBaseUnits(amount, currencyCode);
			const isBonusCpm = bonusCpm && bonusThreshold ? amountInBaseUnits >= bonusThreshold : false;
			const baseImpressionCount = calculateImpressions(amountInBaseUnits, cpm);
			const bonusImpressionCount = calculateImpressions(amountInBaseUnits, cpm, bonusCpm, bonusThreshold);
			const isDouble = isBonusCpm && bonusCpm ? cpm / bonusCpm === 2 : false;
			const impressionCount = isBonusCpm ? bonusImpressionCount : baseImpressionCount;
			return {
				label: translatePlural('corgi.co.promote.impression_button_label', impressionCount, {
					impressionCount: localizeNumber(impressionCount),
				}),
				valueDisplay: localizeCurrency(amount, currencyCode),
				metaText: isBonusCpm ? localizeNumber(baseImpressionCount) : undefined,
				bonusLabel: isBonusCpm
					? translate(`fe.pages.sponsors.amount_button_impressions_badge_label.${isDouble ? 'double' : 'bonus'}`)
					: undefined,
			};
		},
		[
			amountToBaseUnits,
			bonusCpm,
			bonusThreshold,
			cpm,
			currencyCode,
			localizeCurrency,
			localizeNumber,
			translate,
			translatePlural,
		],
	);

	return amountChoices.map((amount) => ({
		value: `${amount}`,
		...impressionCountData(amount),
	}));
}
